import { portalDomain, blipDomain, defaultClusterName } from 'app.constants';
import { SessionFeatures } from './SessionFeatures';
const LOCAL_STORAGE_KEY = 'lastCheck';
const DEFAULT_LOGOUT_TIME_MINUTES = 15;
const CHECK_INACTIVITY_INTERVAL_MS = 5000;
const COOKIE_UPDATE_INTERVAL_MS = 5000;
const MINIMUM_RESET_INTERVAL_MS = 1000;
const IS_DEVELOPMENT = portalDomain === 'localhost:8080';
const DOT_LAST_SUBDOMAINS_PORTAL = IS_DEVELOPMENT ? '.localdev.me' : `.${blipDomain}`;
const options = {
    // expire in 8 hours, as the current session expiration is 8 hours
    expires: new Date(Date.now() + 60 * 8 * 60 * 1000),
    path: '/',
    domain: DOT_LAST_SUBDOMAINS_PORTAL,
    sameSite: 'Lax',
};
const eventListeners = {};
const addEventListener = (target, event, listener, options) => {
    if (!eventListeners[event]) {
        eventListeners[event] = [];
    }
    eventListeners[event].push(listener);
    target.addEventListener(event, listener, options);
};
const removeEventListeners = () => {
    for (const [event, listeners] of Object.entries(eventListeners)) {
        listeners.forEach(listener => {
            window.removeEventListener(event, listener);
            document.removeEventListener(event, listener);
        });
    }
    for (const event in eventListeners) {
        if (eventListeners.hasOwnProperty(event)) {
            eventListeners[event] = [];
        }
    }
};
const checkInactivity = async (timeToLogoutInMinutes) => {
    const currentDate = Date.now();
    const lastStoredActivity = new Date(parseInt(getCookie(LOCAL_STORAGE_KEY))).getTime() || 0;
    const timeToLogoutInMs = timeToLogoutInMinutes * 60 * 1000;
    const shouldLogout = lastStoredActivity && currentDate - lastStoredActivity > timeToLogoutInMs;
    if (shouldLogout) {
        await onLogout();
        await trackLogoutInactivity(timeToLogoutInMinutes);
        removeEventListeners();
        window.location.href = '/logout';
    }
};
export const onLogout = async () => {
    const isEnabled = await isSessionExpirationEnabled();
    if (!isEnabled) {
        return;
    }
    eraseCookie(LOCAL_STORAGE_KEY);
};
const trackLogoutInactivity = async (timeToLogoutInMinutes) => {
    const email = localStorage.getItem('apc_user_id');
    const fullPayload = {
        cluster: defaultClusterName,
        email,
        timeToLogoutInMinutes,
    };
    await analytics.track('portal-user-logout-due-to-inactivity', fullPayload);
};
const isSessionExpirationEnabled = async () => {
    const sessionInactivityConfiguration = await SessionFeatures.isSessionExpirationEnabled();
    const cluster = defaultClusterName;
    return sessionInactivityConfiguration?.isEnabled
        ? sessionInactivityConfiguration.clusters?.includes(cluster) ?? false
        : false;
};
export const sessionExpiry = async () => {
    const isEnabled = await isSessionExpirationEnabled();
    if (!isEnabled) {
        return;
    }
    const { inactivityMinutesToLogout } = await SessionFeatures.isSessionExpirationEnabled();
    const timeToLogoutInMinutes = inactivityMinutesToLogout || DEFAULT_LOGOUT_TIME_MINUTES;
    let lastTimerTriggered = Date.now();
    let lastStorageWrite = Date.now();
    checkInactivity(timeToLogoutInMinutes);
    setCookie(LOCAL_STORAGE_KEY, lastStorageWrite.toString(), options);
    const storeLastActivity = () => {
        const currentDate = Date.now();
        if (currentDate - lastStorageWrite > COOKIE_UPDATE_INTERVAL_MS) { // store only if it's been more than 5 seconds to avoid too many writes
            lastStorageWrite = currentDate;
            setCookie(LOCAL_STORAGE_KEY, Date.now().toString(), options);
        }
    };
    let interval = setInterval(() => checkInactivity(timeToLogoutInMinutes), CHECK_INACTIVITY_INTERVAL_MS);
    const resetTimer = () => {
        const currentDate = Date.now();
        const timeSinceLastReset = currentDate - lastTimerTriggered;
        // if the last reset was less than 1 second ago, don't do anything
        if (timeSinceLastReset < MINIMUM_RESET_INTERVAL_MS) {
            return;
        }
        storeLastActivity();
        lastTimerTriggered = currentDate;
        clearInterval(interval);
        interval = setInterval(() => checkInactivity(timeToLogoutInMinutes), CHECK_INACTIVITY_INTERVAL_MS);
    };
    addEventListener(window, 'load', resetTimer, true);
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'touchmove', 'click', 'keydown', 'wheel'];
    events.forEach((name) => {
        addEventListener(document, name, resetTimer, true);
    });
};
const setCookie = (name, value, options) => {
    let cookieString = `${name}=${value}`;
    if (options.expires) {
        cookieString += `; expires=${options.expires}`;
    }
    if (options.path) {
        cookieString += `; path=${options.path}`;
    }
    if (options.domain) {
        cookieString += `; domain=${options.domain}`;
    }
    if (options.sameSite) {
        cookieString += `; SameSite=${options.sameSite}`;
    }
    document.cookie = cookieString;
};
const getCookie = (name) => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) == ' ') {
            c = c.substring(1, c.length);
        }
        if (c.indexOf(nameEQ) == 0) {
            return c.substring(nameEQ.length, c.length);
        }
    }
    return undefined;
};
const eraseCookie = (name) => {
    document.cookie = `${name}=; path=/; domain=${DOT_LAST_SUBDOMAINS_PORTAL}; Expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
};
